import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Gift, Tag, Receipt, IndianRupee, Clock, CalendarDays } from 'lucide-react';
import { DatePicker } from '../DatePicker';
import { GuestPicker } from '../GuestPicker';
import { HourlySlotPicker } from './HourlySlotPicker';

/**
 * Sticky right-side booking widget on the hotel detail page.
 * Owns NO state — all values + setters come from parent.
 */
export const BookingWidget = ({
  hotel,
  bookingData,
  setBookingData,
  selectedRoom,
  nights,
  grossTotal,
  appliedRedeem,
  total,
  booking,
  handleBooking,
  guestCode,
  setGuestCode,
  walletInfo,
  redeemAmount,
  setRedeemAmount,
  validateCode,
}) => {
  const hourlyAvailable = !!selectedRoom?.hourly_pricing_enabled;
  const isHourly = bookingData.booking_type === 'hourly';
  const setMode = (mode) => {
    if (mode === 'hourly' && !hourlyAvailable) return;
    setBookingData({
      ...bookingData,
      booking_type: mode,
      slots: [],
      slot_total_amount: 0,
      ...(mode === 'hourly' ? { check_out: bookingData.check_in || '' } : {}),
    });
  };
  return (
    <div className="lg:sticky lg:top-24 bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-sm" data-testid="booking-widget">
      <div className="flex items-baseline gap-1 mb-1">
        {isHourly ? (
          <>
            <span className="text-3xl font-black text-black">₹{Number(bookingData.slot_total_amount || 0).toLocaleString('en-IN')}</span>
            <span className="text-sm text-gray-500">/ {(bookingData.slots || []).length || 0} slot{(bookingData.slots || []).length === 1 ? '' : 's'}</span>
          </>
        ) : (
          <>
            <span className="text-3xl font-black text-black">₹{(selectedRoom?.base_price || 0).toLocaleString('en-IN')}</span>
            <span className="text-sm text-gray-500">/night</span>
          </>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">Pay at hotel · Free cancellation</p>

      {hourlyAvailable && (
        <div className="grid grid-cols-2 gap-1 p-1 bg-[#FAFAFA] border border-gray-200 rounded-xl mb-4" data-testid="booking-mode-toggle">
          <button
            type="button"
            onClick={() => setMode('full_day')}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-black transition-colors ${
              !isHourly ? 'bg-[#FF0059] text-white shadow-sm' : 'text-gray-600 hover:bg-white'
            }`}
            data-testid="mode-full-day"
          >
            <CalendarDays className="h-3.5 w-3.5" /> Full day
          </button>
          <button
            type="button"
            onClick={() => setMode('hourly')}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-black transition-colors ${
              isHourly ? 'bg-[#FF0059] text-white shadow-sm' : 'text-gray-600 hover:bg-white'
            }`}
            data-testid="mode-hourly"
          >
            <Clock className="h-3.5 w-3.5" /> Hourly slots
          </button>
        </div>
      )}

      <form onSubmit={handleBooking} className="space-y-3" data-testid="booking-form">
        <div className="space-y-2 p-3 border border-gray-200 rounded-xl">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Room</Label>
          <select
            className="w-full text-sm font-bold text-black focus:outline-none bg-transparent"
            value={bookingData.room_type_id}
            onChange={(e) => setBookingData({ ...bookingData, room_type_id: e.target.value })}
            required
            data-testid="room-type-select"
          >
            {hotel.room_types?.map((r) => {
              const label = `${r.name} — ₹${r.base_price.toLocaleString('en-IN')}`;
              return <option key={r._id} value={r._id} label={label}>{label}</option>;
            })}
          </select>
        </div>

        {isHourly ? (
          <>
            <div className="p-3 border border-gray-200 rounded-xl">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</Label>
              <DatePicker
                value={bookingData.check_in}
                onChange={(iso) => setBookingData({ ...bookingData, check_in: iso, check_out: iso, slots: [], slot_total_amount: 0 })}
                minDate={new Date()}
                placeholder="DD-MM-YYYY"
                testId="hourly-date-input"
              />
            </div>
            <div className="p-3 border border-gray-200 rounded-xl space-y-2">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <Clock className="h-3 w-3 text-[#FF0059]" /> Pick your slots
              </Label>
              <HourlySlotPicker
                roomId={selectedRoom?._id}
                date={bookingData.check_in}
                selectedSlots={bookingData.slots || []}
                onChange={({ slots, total }) => setBookingData({ ...bookingData, slots, slot_total_amount: total })}
              />
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div className="p-3 border border-gray-200 rounded-xl">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Check-in</Label>
              <DatePicker
                value={bookingData.check_in}
                onChange={(iso) => setBookingData({ ...bookingData, check_in: iso, check_out: bookingData.check_out && iso && bookingData.check_out < iso ? '' : bookingData.check_out })}
                minDate={new Date()}
                placeholder="DD-MM-YYYY"
                testId="check-in-input"
              />
            </div>
            <div className="p-3 border border-gray-200 rounded-xl">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Check-out</Label>
              <DatePicker
                value={bookingData.check_out}
                onChange={(iso) => setBookingData({ ...bookingData, check_out: iso })}
                minDate={bookingData.check_in ? new Date(bookingData.check_in + 'T00:00:00') : new Date()}
                placeholder="DD-MM-YYYY"
                testId="check-out-input"
              />
            </div>
          </div>
        )}

        <div className="p-3 border border-gray-200 rounded-xl">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Guests</Label>
          <GuestPicker
            adults={bookingData.adults || 1}
            children={bookingData.children || 0}
            onChange={({ adults, children }) => setBookingData({ ...bookingData, adults, children })}
            testIdPrefix="booking"
            compact
          />
        </div>

        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Tag className="h-3 w-3 text-[#FF0059]" /> Have a Guest Code?
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="e.g. AA12345SH"
              value={guestCode}
              onChange={(e) => setGuestCode(e.target.value.toUpperCase())}
              maxLength={9}
              className="uppercase tracking-widest font-mono font-bold"
              data-testid="guest-code-input"
            />
            <Button
              type="button"
              onClick={validateCode}
              variant="outline"
              className="font-bold border-[#FF0059] text-[#FF0059] hover:bg-[#FFE5EE]"
              data-testid="apply-guest-code"
            >
              Apply
            </Button>
          </div>
          {walletInfo && walletInfo.balance > 0 && (
            <div className="mt-2 p-3 bg-[#FFE5EE] rounded-xl border border-[#FF0059]/30" data-testid="wallet-redeem-row">
              <p className="text-xs font-bold text-[#CC0047] flex items-center gap-1">
                <Gift className="h-3 w-3" /> {walletInfo.owner_name}&apos;s wallet · ₹{walletInfo.balance.toLocaleString('en-IN')} available
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs font-bold text-gray-600">Redeem ₹</span>
                <input
                  type="number"
                  min="0"
                  max={Math.min(walletInfo.balance, grossTotal)}
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="flex-1 px-2 py-1 rounded border border-[#FF0059]/30 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#FF0059]/40"
                  data-testid="redeem-amount-input"
                />
                <button
                  type="button"
                  onClick={() => setRedeemAmount(Math.min(walletInfo.balance, grossTotal))}
                  className="text-[10px] font-bold text-[#FF0059] hover:underline"
                  data-testid="redeem-max"
                >
                  MAX
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Guest details</p>
          <div className="space-y-2">
            <Input placeholder="Full name" value={bookingData.guest_name} onChange={(e) => setBookingData({ ...bookingData, guest_name: e.target.value })} required data-testid="guest-name-input" />
            <Input type="email" placeholder="Email" value={bookingData.guest_email} onChange={(e) => setBookingData({ ...bookingData, guest_email: e.target.value })} required data-testid="guest-email-input" />
            <Input type="tel" placeholder="Phone (+91...)" value={bookingData.guest_phone} onChange={(e) => setBookingData({ ...bookingData, guest_phone: e.target.value })} required data-testid="guest-phone-input" />
          </div>
        </div>

        {((isHourly && (bookingData.slots || []).length > 0) || (!isHourly && nights > 0 && selectedRoom)) && (
          <div className="bg-[#FAFAFA] rounded-xl p-3 text-sm" data-testid="booking-summary">
            {isHourly ? (
              <div className="flex justify-between text-gray-600">
                <span>{(bookingData.slots || []).length} hourly slot{(bookingData.slots || []).length === 1 ? '' : 's'}</span>
                <span className="font-bold text-black">₹{grossTotal.toLocaleString('en-IN')}</span>
              </div>
            ) : (
              <div className="flex justify-between text-gray-600">
                <span>₹{selectedRoom.base_price.toLocaleString('en-IN')} × {nights} {nights === 1 ? 'night' : 'nights'}</span>
                <span className="font-bold text-black">₹{grossTotal.toLocaleString('en-IN')}</span>
              </div>
            )}
            {appliedRedeem > 0 && (
              <div className="flex justify-between text-[#FF0059] mt-1" data-testid="wallet-discount-row">
                <span className="flex items-center gap-1"><Gift className="h-3 w-3" /> Wallet discount</span>
                <span className="font-bold">-₹{appliedRedeem.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between mt-2 pt-2 border-t border-gray-200 font-black">
              <span>Pay at hotel</span>
              <span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold py-6 rounded-xl text-base"
          disabled={booking || !hotel.room_types || hotel.room_types.length === 0}
          data-testid="book-now-button"
        >
          {booking ? 'Booking...' : 'Reserve · Pay at Hotel'}
        </Button>
        <p className="text-[10px] text-center text-gray-500">You won&apos;t be charged yet — pay on arrival</p>

        <div className="mt-3 space-y-2" data-testid="booking-notes">
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FFF9DB] border border-[#FFCB00]/40" data-testid="gst-note">
            <IndianRupee className="h-3.5 w-3.5 text-[#997800] flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-snug text-[#5c4900] font-semibold">
              Additional 5% GST applied on total booking amount will collected at hotel at the time of payments
            </p>
          </div>
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FFE5EE] border border-[#FF0059]/30" data-testid="invoice-note">
            <Receipt className="h-3.5 w-3.5 text-[#CC0047] flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-snug text-[#CC0047] font-semibold">
              Guest will get Invoices or Bills at hotel counter on demand
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};
